import React from 'react';
import { Reveal } from '../components/Reveal';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Droplets, Clock, Car, Accessibility, Bus, Sparkles, Shield, TrendingDown, CreditCard, Smartphone, Wallet, Play, MapPin, Info, Wine, FlaskConical, Recycle, Package, ShoppingBag, Lightbulb, Download, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSection, resolveImage } from '../contexts/ContentContext';
import PauseOnScroll from '../components/PauseOnScroll';

const PAY_ICON = { CreditCard, Smartphone };
const CONTAINER_ICON = { Wine, Droplets, FlaskConical, Recycle };

export default function HowItWorks() {
  const c = useSection('howItWorks');
  const hero = c.hero || {};
  const inst = c.installation || {};
  const acc = c.accessibility || {};
  const q = c.waterQuality || {};
  const comp = c.comparison || {};
  const vid = c.video || {};
  const pr = c.pricing || {};
  const pay = c.payment || {};
  const vol = c.volumes || {};
  const containers = c.containers || {};
  const kit = c.kit || {};
  const finalCta = c.finalCta || {};

  return (
    <>
      {/* HERO */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50 relative overflow-hidden">
        <div className="absolute top-32 -left-32 w-96 h-96 bg-sky-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -right-32 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {hero.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">{hero.eyebrow}</span>}
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-6 leading-tight">
              {hero.title} <span className="gradient-text">{hero.titleHighlight}</span>{hero.titleSuffix}
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">{hero.description}</p>
          </Reveal>
        </div>
      </section>

      {/* INSTALLATION - Premier kiosque */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
                <MapPin className="w-3.5 h-3.5" />Première installation
              </span>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-6">{inst.title}</h2>
              {(inst.paragraphs || []).map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
              ))}
              <div className="mt-6 inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-100 text-sm">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-sm">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-bold text-sky-950">Depuis le 21 mai 2024</p>
                  <p className="text-slate-500 text-xs">Carrosserie TAC · 23 bd Charles-de-Gaulle · 64140 Lons</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center border-4 border-white ring-1 ring-sky-100">
                {inst.photo ? (
                  <img src={resolveImage(inst.photo)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center p-8">
                    <Car className="w-16 h-16 text-sky-400 mx-auto mb-3" />
                    <p className="text-sky-700 font-semibold">Photo à téléverser depuis le CMS</p>
                    <p className="text-xs text-slate-500 mt-1">Carrosserie T.A.C · Coccinelle Araignée</p>
                  </div>
                )}
              </div>
              {inst.photoCaption && <p className="text-xs text-slate-500 mt-3 text-center italic">{inst.photoCaption}</p>}
            </Reveal>
          </div>
        </div>
      </section>

      {/* ACCESSIBILITY */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-3">{acc.title}</h2>
              <p className="text-slate-600">{acc.hoursText}</p>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-3 gap-6 mb-10">
            <Reveal>
              <div className="p-7 bg-white rounded-3xl border border-sky-100 h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center mb-5 shadow-md">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-sky-950 mb-3">Horaires libres</h3>
                <p className="text-slate-600 text-sm leading-relaxed">24 heures sur 24, 7 jours sur 7. Aucune contrainte d'ouverture, à toute heure du jour ou de la nuit.</p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="p-7 bg-white rounded-3xl border border-sky-100 h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-5 shadow-md">
                  <Car className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-sky-950 mb-3">Stationnement facile</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-3">{acc.parkingIntro}</p>
                <ul className="space-y-2">
                  {(acc.parkingItems || []).map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="p-7 bg-white rounded-3xl border border-sky-100 h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mb-5 shadow-md">
                  <Accessibility className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display font-bold text-xl text-sky-950 mb-3">PMR & transports</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{acc.pmrText}</p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold">
                  <Bus className="w-3.5 h-3.5" />Arrêt Campagnol · ligne 3C · 90 m
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="grid sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {(acc.photos || ['', '']).map((ph, i) => (
                <div key={i} className="aspect-square rounded-3xl overflow-hidden shadow-lg bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center border-4 border-white">
                  {ph ? <img src={resolveImage(ph)} alt="" className="w-full h-full object-cover" /> : (
                    <div className="text-center p-6"><Droplets className="w-12 h-12 text-sky-400 mx-auto mb-2" /><p className="text-xs text-sky-700 font-semibold">Photo du kiosque {i + 1}</p></div>
                  )}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* WATER QUALITY */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
                <Sparkles className="w-3.5 h-3.5" />Qualité
              </span>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-4">{q.title}</h2>
              <p className="text-slate-600 leading-relaxed">{q.description}</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {(q.features || []).map((f, i) => (
              <Reveal key={i} delay={i * 70}>
                <div className="p-5 rounded-2xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 text-center h-full">
                  <div className="w-12 h-12 rounded-full bg-white shadow mx-auto mb-3 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  </div>
                  <p className="font-semibold text-sky-950 text-sm">{f}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-5">
            <Reveal>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-950 to-emerald-950 text-white h-full">
                <Shield className="w-8 h-8 text-emerald-300 mb-4" />
                <h3 className="font-display font-bold text-lg mb-3">Purification</h3>
                <p className="text-sm text-sky-100 leading-relaxed">{q.purification}</p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100 h-full">
                <Sparkles className="w-8 h-8 text-sky-700 mb-4" />
                <h3 className="font-display font-bold text-lg text-sky-950 mb-3">Pétillant fin & léger</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{q.sparklingNote}</p>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="p-6 rounded-3xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 h-full">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-5 h-5 text-emerald-700" />
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Nouvelle génération</span>
                </div>
                <h3 className="font-display font-bold text-lg text-sky-950 mb-3">À partir d'octobre 2026</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{q.futureNote}</p>
              </div>
            </Reveal>
          </div>

          {comp.text && (
            <Reveal>
              <div className="mt-10 p-6 rounded-3xl bg-white border border-sky-100 text-center max-w-3xl mx-auto shadow-sm">
                <p className="text-slate-700 leading-relaxed italic">« {comp.text} »</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* VIDEO */}
      <section className="py-16 bg-gradient-to-br from-sky-950 to-emerald-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative aspect-video rounded-3xl overflow-hidden bg-black/40 flex items-center justify-center border border-white/10">
              <PauseOnScroll className="w-full h-full">
              {vid.url ? (
                /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(vid.url) ? (
                  <video
                    src={vid.url}
                    controls
                    playsInline
                    className="w-full h-full object-cover bg-black"
                    data-testid="howitworks-video-player"
                  />
                ) : (
                  <iframe
                    src={vid.url}
                    title={vid.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="autoplay; fullscreen"
                    data-testid="howitworks-video-iframe"
                  />
                )
              ) : (
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur flex items-center justify-center mx-auto mb-4 hover:bg-white/20 transition-colors cursor-pointer">
                    <Play className="w-8 h-8 text-white ml-1 fill-white" />
                  </div>
                  <p className="font-display font-bold text-xl">{vid.title || 'Vidéo'}</p>
                  <p className="text-sm text-sky-300 mt-1">{vid.placeholder}</p>
                </div>
              )}
              </PauseOnScroll>
            </div>
            {vid.caption && (
              <p className="mt-5 text-center text-sm lg:text-base text-sky-200 italic max-w-3xl mx-auto" data-testid="howitworks-video-caption">
                {vid.caption}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
                <Wallet className="w-3.5 h-3.5" />Tarifs
              </span>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-4">{pr.title}</h2>
              <p className="text-slate-600">{pr.intro}</p>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto mb-12">
            {(pr.kiosque || []).map((k, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className={`p-8 rounded-3xl text-white text-center shadow-xl ${i === 0 ? 'bg-gradient-to-br from-sky-500 to-sky-700' : 'bg-gradient-to-br from-emerald-500 to-teal-600'}`}>
                  <Droplets className="w-10 h-10 mx-auto mb-3 opacity-80" />
                  <p className="text-sm uppercase tracking-wider font-semibold opacity-90">{k.label}</p>
                  <p className="font-display font-extrabold text-4xl lg:text-5xl mt-2">{k.price}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <TrendingDown className="w-5 h-5 text-rose-500" />
                <h3 className="font-display font-bold text-lg text-sky-950">{pr.comparisonTitle}</h3>
              </div>
              <div className="rounded-2xl border border-sky-100 overflow-hidden divide-y divide-sky-50">
                {(pr.comparisons || []).map((cmp, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 p-4 bg-white hover:bg-sky-50/40 transition-colors">
                    <span className="text-sm text-slate-700">{cmp.label}</span>
                    <span className="font-display font-bold text-rose-600 text-sm sm:text-base">{cmp.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PAYMENT */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-3">{pay.title}</h2>
              <p className="text-slate-600">{pay.intro}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {(pay.options || []).map((o, i) => {
              const Icon = PAY_ICON[o.icon] || CreditCard;
              return (
                <Reveal key={i} delay={i * 100}>
                  <div className="p-7 rounded-3xl bg-white border border-sky-100 h-full hover:shadow-lg transition-shadow flex flex-col">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${i === 0 ? 'bg-gradient-to-br from-sky-400 to-sky-600' : 'bg-gradient-to-br from-emerald-400 to-teal-500'}`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-display font-bold text-xl text-sky-950 mb-3">{o.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{o.text}</p>
                    {o.downloadUrl && (
                      <a
                        href={o.downloadUrl}
                        download={o.downloadFilename || ''}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-5 inline-flex items-center gap-2 self-start px-4 py-2.5 rounded-xl bg-emerald-50 text-emerald-700 font-semibold text-sm border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                        data-testid={`payment-option-download-${i}`}
                      >
                        <Download className="w-4 h-4" />
                        {o.downloadLabel || 'Télécharger le fichier pour en savoir plus'}
                      </a>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VOLUMES */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-3">{vol.title}</h2>
              <p className="text-slate-600">{vol.intro}</p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <Reveal>
              <div className="p-6 rounded-3xl border-2 border-sky-200 bg-gradient-to-br from-white to-sky-50">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-sky-950">Eau pétillante</h3>
                </div>
                <div className="space-y-2">
                  {(vol.sparkling || []).map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white border border-sky-100">
                      <span className="font-semibold text-sky-900">{v.volume}</span>
                      <span className="font-display font-bold text-sky-700">{v.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="p-6 rounded-3xl border-2 border-emerald-200 bg-gradient-to-br from-white to-emerald-50">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                    <Droplets className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-xl text-sky-950">Eau nature</h3>
                </div>
                <div className="space-y-2">
                  {(vol.nature || []).map((v, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-white border border-emerald-100">
                      <span className="font-semibold text-sky-900">{v.volume}</span>
                      <span className="font-display font-bold text-emerald-700">{v.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <p className="text-center text-sm text-slate-600 mb-10 italic">{vol.note}</p>
          </Reveal>

          {Array.isArray(vol.tips) && vol.tips.length > 0 && (
            <Reveal>
              <div className="max-w-3xl mx-auto mb-10 p-6 rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-lg text-sky-950">Bon à savoir</h3>
                </div>
                <ul className="space-y-2.5">
                  {vol.tips.map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}

          <Reveal>
            <div className="max-w-3xl mx-auto">
              <div className="aspect-video rounded-3xl overflow-hidden shadow-xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center border-4 border-white">
                {vol.photo ? (
                  <img src={resolveImage(vol.photo)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center p-8">
                    <div className="w-16 h-16 rounded-full bg-yellow-400 flex items-center justify-center mx-auto mb-3 shadow-lg">
                      <div className="w-10 h-10 rounded-full bg-emerald-500 border-4 border-yellow-400"></div>
                    </div>
                    <p className="text-sm text-sky-700 font-semibold">Photo du tableau de commandes</p>
                    <p className="text-xs text-slate-500 mt-1">{vol.photoCaption}</p>
                  </div>
                )}
              </div>
              {vol.photoCaption && <p className="text-xs text-slate-500 mt-3 text-center italic">{vol.photoCaption}</p>}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTAINERS */}
      {containers.title && (
        <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-sky-700 text-xs font-bold uppercase tracking-wider mb-4 shadow-sm">
                  <Package className="w-3.5 h-3.5" />Contenants
                </span>
                <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-3">{containers.title}</h2>
                <p className="text-slate-600">{containers.intro}</p>
              </div>
            </Reveal>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10 max-w-4xl mx-auto">
              {(containers.items || []).map((item, i) => {
                const Icon = CONTAINER_ICON[item.icon] || Droplets;
                return (
                  <Reveal key={i} delay={i * 80}>
                    <div className="p-6 rounded-2xl bg-white border border-sky-100 text-center h-full hover:shadow-md hover:-translate-y-0.5 transition-all">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-7 h-7 text-sky-700" />
                      </div>
                      <p className="font-semibold text-sky-950 text-sm">{item.label}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              <Reveal>
                <div className="p-6 rounded-3xl bg-white border-2 border-emerald-200 h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-5 h-5 text-emerald-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">Recommandé</span>
                  </div>
                  <p className="text-slate-700 leading-relaxed text-sm">{containers.recommendation}</p>
                </div>
              </Reveal>
              <Reveal delay={100}>
                <div className="p-6 rounded-3xl bg-white border border-sky-100 h-full">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-5 h-5 text-sky-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-sky-700">Compatible</span>
                  </div>
                  <p className="text-slate-700 leading-relaxed text-sm">{containers.note}</p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* KIT */}
      {kit.title && (
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-14">
              <Reveal>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-wider mb-4">
                  <ShoppingBag className="w-3.5 h-3.5" />Notre kit
                </span>
                <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-5">{kit.title}</h2>
                <p className="text-slate-600 leading-relaxed mb-5">{kit.intro}</p>
                <ul className="space-y-3 mb-6">
                  {(kit.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-slate-500 italic">{kit.conclusion}</p>
              </Reveal>
              <Reveal delay={120}>
                <div className="aspect-square rounded-[2.5rem] overflow-hidden shadow-xl bg-gradient-to-br from-emerald-100 to-sky-100 border-4 border-white ring-1 ring-sky-100 flex items-center justify-center">
                  {kit.image ? (
                    <img src={resolveImage(kit.image)} alt="Kit Kiosques à Eau" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center p-8">
                      <ShoppingBag className="w-20 h-20 text-sky-500 mx-auto mb-4" />
                      <p className="text-sky-700 font-semibold">Photo du Kit</p>
                      <p className="text-xs text-slate-500 mt-1">À téléverser depuis le CMS</p>
                    </div>
                  )}
                </div>
              </Reveal>
            </div>

            <Reveal>
              <div className="max-w-4xl mx-auto">
                <h3 className="font-display font-bold text-xl text-sky-950 text-center mb-6">{kit.formulasTitle}</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  {(kit.formulas || []).map((f, i) => {
                    const styles = [
                      'from-sky-400 to-sky-600',
                      'from-emerald-400 to-teal-500',
                      'from-purple-400 to-pink-500',
                    ];
                    const href = kit.formulasLink;
                    const CardInner = (
                      <>
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${styles[i % styles.length]} flex items-center justify-center mx-auto mb-4 shadow-md`}>
                          <ShoppingBag className="w-6 h-6 text-white" />
                        </div>
                        <p className="font-semibold text-sky-950 text-sm mb-2 leading-tight">{f.label}</p>
                        <p className="font-display font-extrabold text-3xl gradient-text">{f.price}</p>
                        {href && (
                          <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 group-hover:text-emerald-600 transition-colors">
                            Commander <ExternalLink className="w-3 h-3" />
                          </span>
                        )}
                      </>
                    );
                    if (href) {
                      return (
                        <a
                          key={i}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`howitworks-formula-link-${i}`}
                          aria-label={`${f.label} — ${f.price} — ${kit.formulasLinkLabel || 'Commander'}`}
                          className="group block p-6 rounded-3xl bg-white border-2 border-sky-100 text-center hover:border-emerald-300 hover:shadow-xl hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 transition-all cursor-pointer"
                        >
                          {CardInner}
                        </a>
                      );
                    }
                    return (
                      <div key={i} className="p-6 rounded-3xl bg-white border-2 border-sky-100 text-center hover:shadow-lg hover:-translate-y-1 transition-all">
                        {CardInner}
                      </div>
                    );
                  })}
                </div>
                {kit.formulasLink && (
                  <p className="mt-5 text-center text-xs text-slate-500">
                    Vous serez redirigé vers notre boutique sécurisée HelloAsso.
                  </p>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-sky-950 to-emerald-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Droplets className="w-14 h-14 text-emerald-300 mx-auto mb-6" />
          <h2 className="font-display font-extrabold text-4xl lg:text-5xl mb-5">{finalCta.title}</h2>
          <p className="text-lg text-sky-200 mb-8">{finalCta.description}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact"><Button size="lg" className="bg-white text-sky-900 hover:bg-sky-50 rounded-xl px-7 font-semibold" data-testid="howitworks-cta-contact">Nous contacter<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
          </div>
        </div>
      </section>
    </>
  );
}
